/* eslint-disable react-hooks/immutability -- ReactiveProperty.value is an imperative setter for cross-framework reactivity */
/**
 * useReactiveProperty - React hook for binding to reactive properties
 *
 * This hook allows React components to automatically re-render when a property changes.
 * It integrates with the existing property system via useReactivePropertyManager.
 */

import { useEffect, useState } from 'react';
import { ReactiveProperty } from './ReactiveProperty';
import { PropertyType, PropertyValueData } from '../types';
import { useReactivePropertyManager } from './useReactivePropertyManager';

export interface UseReactivePropertyOptions {
  projectId: string;
  identifier: string;
  objectIdentifier: string;
  propertyType: PropertyType;
}

/**
 * Hook to use a reactive property in a React component
 * Returns [value, setValue, reactiveProperty]
 *
 * @example
 * ```tsx
 * const [tolerance, setTolerance] = useReactiveProperty({
 *   projectId,
 *   identifier: 'surface_tolerance',
 *   objectIdentifier: surfaceId,
 *   propertyType: PropertyType.PROJECT_GLOBAL
 * });
 *
 * // Use in JSX
 * <input value={tolerance} onChange={e => setTolerance(parseFloat(e.target.value))} />
 * ```
 */
export function useReactiveProperty(options: UseReactivePropertyOptions): [PropertyValueData, (value: PropertyValueData) => void, ReactiveProperty<PropertyValueData>] {
  const { projectId, identifier, objectIdentifier, propertyType } = options;

  // Get the property service
  const service = useReactivePropertyManager({ projectId });

  // Get the reactive property
  const property = service.getProperty(identifier, objectIdentifier, propertyType);

  // Create local state that syncs with the reactive property
  const [value, setValue] = useState<PropertyValueData>(property.value);

  // Subscribe to property changes
  useEffect(() => {
    const subscription = property.subscribe((newValue) => {
      setValue(newValue);
    });

    return () => subscription.unsubscribe();
  }, [property]);

  // Setter function
  const setter = (newValue: PropertyValueData) => {
    property.value = newValue;
  };

  return [value, setter, property];
}

/**
 * Hook to get just the value of a reactive property (read-only)
 *
 * @example
 * ```tsx
 * const tolerance = useReactivePropertyValue({
 *   projectId,
 *   identifier: 'surface_tolerance',
 *   objectIdentifier: surfaceId,
 *   propertyType: PropertyType.PROJECT_GLOBAL
 * });
 * ```
 */
export function useReactivePropertyValue(options: UseReactivePropertyOptions): PropertyValueData {
  const [value] = useReactiveProperty(options);
  return value;
}

/**
 * Hook to get the reactive property instance directly
 * Useful when you need the full API (subscribe, value access, etc.)
 *
 * @example
 * ```tsx
 * const toleranceProperty = useReactivePropertyInstance({
 *   projectId,
 *   identifier: 'surface_tolerance',
 *   objectIdentifier: surfaceId,
 *   propertyType: PropertyType.PROJECT_GLOBAL
 * });
 *
 * // Subscribe to changes
 * useEffect(() => {
 *   const sub = toleranceProperty.subscribe(value => {
 *     console.log('Tolerance changed:', value);
 *   });
 *   return () => sub.unsubscribe();
 * }, [toleranceProperty]);
 *
 * // Direct access
 * const current = toleranceProperty.value;
 * ```
 */
export function useReactivePropertyInstance(options: UseReactivePropertyOptions): ReactiveProperty<PropertyValueData> {
  const { projectId, identifier, objectIdentifier, propertyType } = options;
  const service = useReactivePropertyManager({ projectId });
  return service.getProperty(identifier, objectIdentifier, propertyType);
}
